import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isToday from "dayjs/plugin/isToday";
import isTomorrow from "dayjs/plugin/isTomorrow";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import orderBy from "lodash/orderBy";

import type { DateTimeMatrix, TimeSlotsType, Timezone, TimezoneData } from "@app-types/date.type";
import { addLeadingZero } from "./strings.util";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);

export function formatAsIsoString(date: dayjs.ConfigType) {
  return dayjs(date).format();
}

export function isDateOlderThanNDays(date: Date, numberOfDays: number, currentDate: Date) {
  const targetDate = dayjs(currentDate).subtract(numberOfDays, "days");
  return dayjs(date).isSameOrBefore(targetDate);
}

export function findTimezoneByPlace(placeName: string, timezoneList: TimezoneData[]) {
  return timezoneList.find((zone) => zone.places?.some((place) => place.intName === placeName));
}

export function formatDateCustom(date: Date, mask: string) {
  return dayjs(date).format(mask);
}

/**
 * Formats a given date according to a specified mask and timezone.
 *
 * @param {Date} date - The date to format.
 * @param {string} [mask='YYYY-MM-DDThh:mm'] - The format mask to apply to the date. Defaults to 'YYYY-MM-DDThh:mm'.
 * @param {Timezone} [timezone='+00:00'] - The timezone offset to apply. Defaults to '+00:00'.
 * @returns {string} The formatted date string.
 *
 * @example
 * // Format a date in the default timezone
 * const date = new Date('2024-07-02T12:00:00Z');
 * const formattedDate = formatDateByTimezone(date);
 * console.log(formattedDate); // '2024-07-02T12:00'
 *
 * @example
 * // Format a date in a specific timezone
 * const date = new Date('2024-07-02T02:00:00Z');
 * const mask = 'YYYY-MM-DD HH:mm:ss';
 * const timezone = '+01:30';
 * const formattedDate = formatDateByTimezone(date, mask, timezone);
 * console.log(formattedDate); // '2024-07-02 00:30:00'
 */
export function formatDateByTimezone(
  date: dayjs.ConfigType,
  timezone: Timezone = "+00:00",
  mask: string = "YYYY-MM-DDTHH:mm"
): string {
  return dayjs(date).utcOffset(timezone).format(mask);
}

/**
 * Formats a specified date into different formats.
 * @param {Date} date The source date to format.
 * @returns {Object}
 */
export function formatDateTimeMatrix(date: dayjs.ConfigType): DateTimeMatrix {
  const day = dayjs(date);
  const formattedDate = day.format("MMM D [at] h:mm A");
  const formattedDateNoTime = day.format("MMM D");
  const relativeDate = day.fromNow();
  const time = formattedDate.match(/([0-1]?[0-9]|2[0-3]):[0-5][0-9] [AP]M/);

  return {
    formattedDate,
    formattedDateNoTime,
    relativeDate,
    time: time ? time[0] : "",
  };
}

export const formatDateToHours = (timestamp: number) => {
  return dayjs(timestamp).format("HH:mm");
};

export function formatSimpleDateTime(date: dayjs.ConfigType): string {
  return dayjs(date).format("MMM D [at] h:mm A");
}

export const formatDateToMMMDD = (dateString: string): string => {
  return dayjs(dateString).isValid() ? dayjs(dateString).format("MMM DD") : "";
};

export const generateTimezonesWithOffsets = (): TimezoneData[] => {
  const timezoneList = Intl.supportedValuesOf("timeZone");
  const rawTimezones = timezoneList.map((tz: string) => {
    const day = dayjs(new Date()).tz(tz);
    const longTimeZone = new Intl.DateTimeFormat("us-en", {
      timeZone: tz,
      timeZoneName: "longGeneric",
    }).formatToParts();
    return {
      offsetMinutes: day.utcOffset(),
      offsetString: offsetAsGmtString(day.utcOffset()),
      timezoneName: longTimeZone.at(-1)?.value ?? "",
      intlTimezone: tz,
    };
  });

  return flattenTimezones(rawTimezones);
};

export function isAfterToday(dateStr: string) {
  return dayjs(dateStr).isSameOrAfter(dayjs(), "day");
}

export function isYesterday(date: dayjs.ConfigType) {
  const yesterday = dayjs().subtract(1, "day").startOf("day");
  return dayjs(date).isSame(yesterday, "day");
}

export function isLastWeek(date: dayjs.ConfigType) {
  const now = dayjs();
  const startOfLast7Days = now.subtract(7, "days").startOf("day");
  const targetDate = dayjs(date);

  return targetDate.isSameOrAfter(startOfLast7Days) && targetDate.isSameOrBefore(now);
}

export function isLastMonth(date: dayjs.ConfigType) {
  const now = dayjs();
  const startOfLast30Days = now.subtract(30, "days").startOf("day");
  const targetDate = dayjs(date);

  return targetDate.isSameOrAfter(startOfLast30Days) && targetDate.isSameOrBefore(now);
}

export function parseDate(dateStr: string): Date {
  return dayjs(dateStr).toDate();
}

/**
 * The function generates an array of time slots in 15-minute intervals starting from the beginning of
 * the day.
 * @returns An array of time slots in 15-minute intervals starting from the current time of day. Each
 * time slot object in the array contains a label in 'h:mm A' format and a value in 'HH:mm' format.
 */
export function generateTimeSlots(): TimeSlotsType[] {
  const slots = [];

  let time = dayjs().startOf("day");

  for (let i = 0; i < 96; i++) {
    slots.push({
      label: time.format("h:mm A"),
      value: time.format("HH:mm"),
      disabled: false,
    });

    time = time.add(15, "minute");
  }

  return slots;
}

/**
 * Formats a given date into time values suitable for a date picker.
 *
 * This function takes a date as either a string or a Date object and formats it into
 * two time formats: one for display and one for internal use. It returns an object
 * containing both the formatted time label and value.
 *
 * @param {string | Date} date - The date to be formatted. Can be a string or a Date object.
 * @returns {{ label: string, value: string }} An object containing the formatted time label and value.
 */
export function getTimeValueForDatePicker(date: string | Date): { label: string; value: string } {
  const [label, value] = formatDateCustom(new Date(date), "h:mm A|HH:mm").split("|");

  return { label, value };
}

/**
 * The function `getNextAvailable30MinuteTimeSlot` finds the next available 30-minute time slot within
 * a 24-hour period based on the current time and a list of predefined time slots.
 * @param currentTime - The `currentTime` parameter is a `dayjs` object representing the current time.
 * It is used to calculate the next available 30-minute time slot based on this current time.
 * @param {TimeSlotsType[]} timeSlots - TimeSlotsType[] - An array of time slots.
 * @param includePrevious - A boolean to return the previous time slot of the selected one.
 * @returns The function `getNextAvailable30MinuteTimeSlot` returns a `TimeSlotsType` object
 * representing the next available 30-minute time slot within the next 24 hours based on the current
 * time and the provided array of time slots. If no available time slots are found within the next 24
 * hours, an error is thrown with the message "No available time slots found within the next 24 hours."
 */
export function getNextAvailable30MinuteTimeSlot(
  currentTime: dayjs.Dayjs,
  timeSlots: TimeSlotsType[],
  includePrevious: boolean = false
): { previous: TimeSlotsType | null; next: TimeSlotsType } {
  let adjustedTime =
    currentTime.minute() % 30 === 0
      ? currentTime
      : currentTime.add(30 - (currentTime.minute() % 30), "minutes");
  adjustedTime = adjustedTime.second(0);

  let previousOption: TimeSlotsType | undefined = undefined;

  /* Maximum possible limit of 96 iterations (24 hour time slots). */
  for (let i = 0; i < 96; i++) {
    const formattedTime = adjustedTime.format("HH:mm");
    const foundOption = timeSlots.find((timeSlot) => timeSlot.value === formattedTime);

    if (foundOption) {
      if (includePrevious) {
        const previousIndex =
          timeSlots.findIndex((timeSlot) => timeSlot.value === formattedTime) - 1;
        previousOption = previousIndex >= 0 ? timeSlots[previousIndex] : undefined;

        return { previous: previousOption ?? null, next: foundOption };
      }
      return { previous: null, next: foundOption };
    }

    adjustedTime = adjustedTime.add(30, "minutes");
  }

  throw new Error("No available time slots found within the next 24 hours.");
}

/**
 * Calculates the meeting time based on the start time and an optional number of days offset.
 *
 * @param start_time - The start time of the meeting in string format.
 * @param daysOffset - The number of days to offset from the current date (default is 7).
 * @param utcOffset - The UTC offset to use for the formatted time (default is +00:00).
 * @returns The formatted meeting time string.
 */
export const getRelativeTime = (
  start_time: dayjs.ConfigType,
  daysOffset = 7,
  utcOffset = "+00:00"
) => {
  const day = dayjs(start_time).utcOffset(utcOffset);
  const now = dayjs().utcOffset(utcOffset);

  const minutesDiff = now.diff(day, "minute");
  if (minutesDiff > -1 && minutesDiff < 1) return "just now";

  const previousDays = now.subtract(daysOffset + 1, "day").startOf("day");
  const nextDays = now.add(daysOffset + 1, "day").startOf("day");

  const isWithinDateRange = day.isBetween(previousDays, nextDays);

  if (isWithinDateRange) return day.fromNow();

  return day.format("MMM D, YYYY [at] h:mm A");
};

/**
 * Calculates the start Date and last Date based of number of days.
 *
 * @param days - The number of days to offset from the current date.
 * @returns The object with startDate and lastDate formatted to ISO String.
 */
export function getDateRangeByDays(days: number) {
  const now = new Date();
  const lastDate = new Date(now);
  const startDate = new Date(now);

  startDate.setDate(now.getDate() - days);

  return {
    startDate: startDate.toISOString(),
    lastDate: lastDate.toISOString(),
  };
}

/*
    Private Functions
*/

const flattenTimezones = (timezoneList: TimezoneData[]): TimezoneData[] => {
  let flattenedTimezones: TimezoneData[] = [];
  timezoneList.forEach((tz: TimezoneData) => {
    const existingTimezone = flattenedTimezones.find(
      (existing) =>
        existing.timezoneName === tz.timezoneName && existing.offsetMinutes === tz.offsetMinutes
    );
    if (existingTimezone === undefined) {
      flattenedTimezones = [
        ...flattenedTimezones,
        {
          offsetMinutes: tz.offsetMinutes,
          offsetString: tz.offsetString,
          places: timezoneList
            .filter(
              (zone) =>
                zone.offsetMinutes === tz.offsetMinutes && zone.timezoneName === tz.timezoneName
            )
            .map((z) => parseIntlName(z.intlTimezone as string)),
          timezoneName: tz.timezoneName,
        },
      ];
    }
  });

  return orderBy(flattenedTimezones, ["offsetMinutes", "timezoneName"]);
};

const offsetAsGmtString = (offset: number): Timezone => {
  const remainder = Math.abs(offset) % 60;
  const hours = (Math.abs(offset) - remainder) / 60;
  const isPositiveOffset = offset >= 0;
  return `${isPositiveOffset ? "+" : "-"}${addLeadingZero(hours.toString(), 2)}:${addLeadingZero(
    remainder.toString(),
    2
  )}` as Timezone;
};

const parseIntlName = (name?: string): { intName: string; friendlyName: string } => {
  if (!name) {
    return { intName: "", friendlyName: "" };
  }
  const parts = name.split("/");
  const cityName = parts.at(-1);
  return {
    intName: name,
    friendlyName: cityName ? cityName.replace("_", " ") : "",
  };
};

export const calculateRelativeTime = (date: string): string => {
  const now = dayjs();
  const givenDate = dayjs(date);
  const diffInSeconds = now.diff(givenDate, "second");
  const diffInMinutes = now.diff(givenDate, "minute");
  const diffInHours = now.diff(givenDate, "hour");

  if (diffInSeconds < 60) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  } else {
    return givenDate.format("MMM D [at] h:mm A");
  }
};

/**
 * Formats a given meeting date into a human-readable string.
 *
 * The function takes a date (either as a string or a Date object) and returns a string
 * that represents how long ago the meeting date was from the current date. The format
 * of the returned string varies based on the difference between the current date and
 * the meeting date:
 *
 * - If the meeting date is in the future, it returns the date in 'MMM DD' or 'MMM DD, YYYY' format.
 * - If the meeting date is less than 60 minutes ago, it returns the difference in minutes.
 * - If the meeting date is less than 24 hours ago, it returns the difference in hours.
 * - If the meeting date was yesterday, it returns 'Yesterday'.
 * - Otherwise, it returns the date in 'MMM DD' or 'MMM DD, YYYY' format.
 *
 * @param date - The date of the meeting, either as a string or a Date object.
 * @param timezone - The timezone offset to apply.
 * @returns A formatted string representing the meeting date.
 */
export const formatDateAgo = (date: string | Date, timezone?: Timezone): string => {
  const dayjsDate = timezone ? dayjs(date).utcOffset(timezone) : dayjs(date);
  const today = timezone ? dayjs().utcOffset(timezone) : dayjs();

  const diffInMinutes = today.diff(dayjsDate, "minute");
  const diffInHours = today.diff(dayjsDate, "hour");
  const diffInDays = today.diff(dayjsDate, "day");

  let dateString = "";

  if (diffInMinutes < 0) {
    dateString = dayjsDate.format("MMM DD, YYYY");
  } else if (diffInMinutes < 60) {
    dateString =
      diffInMinutes === 0 ? "now" : `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  } else if (diffInHours < 24) {
    dateString = `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  } else if (diffInDays < 2) {
    dateString = "Yesterday";
  } else {
    dateString = dayjsDate.format("MMM DD, YYYY");
  }

  return dateString;
};

export default formatDateAgo;

/**
 * Formats an ISO date string to a long date string in the format 'MMM D, YYYY'.
 *
 * @param isoDateString - The ISO date string to format.
 * @returns The formatted date string.
 */
export function formatDateToLongString(isoDateString: string): string {
  return dayjs.utc(isoDateString).format("MMM D, YYYY");
}
